import { useRing } from "../ringContext";
import { useCaps } from "../../editor/CapsProvider";
import { ProfileSection } from "../icons";
import { quarterGrooveSlider } from "../junctionUi";
import { Chip, FileButton, MixedOptionRow, OptionCard, SectionLabel, Slider, Toggle } from "../../kit";
import type {
  BandStyle, EdgeSides, EdgeStyle, GroovePattern, RingShape, SideEdgeStyle,
} from "../../../shared/api-contract";
import {
  currentStep,
  isParamProfile,
  paramPatch,
  paramResetPatch,
  paramSectionPath,
  paramSliders,
  resolvedParam,
  selectParamPatch,
  stepOffPatch,
  stepOnPatch,
  stepPatch,
  stepSliders,
} from "./bodyUi";

/**
 * Ring Builder › Profile — the band's shape and surface: silhouette, twist
 * construction, cross-section, fit and edge/groove treatment.
 *
 * Ports the band Profile step verbatim and adds the v37 silhouette + comfort-fit
 * controls. ⚠️ The three engagement shank rows (Style / Cathedral / Fit) used to
 * be here and moved to Engagement › Band in W22 — see the note where they were.
 * Joint-groove cross-sections
 * (`setJointGrooves`) live in the Joints tab; the split seam path
 * (`setDivision`) lives with the metal split in the Material tab.
 */
export function ProfileTab() {
  const { state, catalogs, r, apply } = useRing();
  const caps = useCaps();
  const crossover = state.bandStyle === "crossover";
  // v37 catalogs are optional (a v36 viewer omits them); caps already gates the
  // RPC, this guards the data.
  const ringShapes = caps.ringBuilderV37 ? (catalogs.ringShapes ?? []) : [];

  return (
    <div className="flex flex-col gap-5">
      <ProfileSummary />

      {/* ── Silhouette (v37) ──
          Cross-over builds through `buildTwistedBand`, whose params carry
          neither `shape` nor `comfortFitMM` — so both controls are dimmed there
          rather than left live and inert. */}
      {ringShapes.length > 0 ? (
        <div className={crossover ? "pointer-events-none opacity-40" : ""}>
          <MixedOptionRow
            label="Silhouette"
            options={ringShapes}
            selected={state.shape ?? "classic"}
            onSelect={(id) => void apply(r.setRingShape(id as RingShape))}
          />
        </div>
      ) : null}

      {/* ── Construction: standard lathe profile vs twisted cross-over shank ── */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Construction</SectionLabel>
        <div className="flex gap-2">
          <Chip
            selected={!crossover}
            onClick={() => void apply(r.setBandStyle("standard" as BandStyle))}
            className="flex-1"
          >
            Standard
          </Chip>
          <Chip
            selected={crossover}
            onClick={() => void apply(r.setBandStyle("crossover" as BandStyle))}
            className="flex-1"
          >
            Cross-over
          </Chip>
        </div>
        {crossover ? (
          <div className="mt-1 flex flex-col gap-3">
            {/* Strands is a 3-way choice, not a continuous quantity — a slider
                with two reachable stops was always a chip row wearing the wrong
                control. 1 is genuinely buildable: `makeStrandCurve` clamps with
                `Math.max(1, Math.round(strands))`, so one strand is a single
                helical tube (a twist, not a braid).

                NEAREST wins rather than strict equality: `setTwist` does NOT
                clamp or round (BandConfigurator.setTwist is a bare assign), and
                the AI designer's `num()` clamps without rounding — so a stored
                `strands: 2.6` is reachable, and strict equality would light no
                chip at all while the builder rounds it to 3. Same reasoning as
                the Partition row in MaterialTab. */}
            <div className="flex flex-col gap-2">
              <SectionLabel>Strands</SectionLabel>
              <div className="flex gap-2">
                {[1, 2, 3].map((n) => {
                  const live = Math.max(1, Math.min(3, Math.round(state.twist.strands)));
                  return (
                    <Chip
                      key={n}
                      selected={live === n}
                      onClick={() => void apply(r.setTwist({ strands: n }))}
                      className="flex-1"
                    >
                      {n}
                    </Chip>
                  );
                })}
              </div>
            </div>
            <Slider
              label="Twists" min={2} max={12} step={1}
              value={state.twist.twists}
              onChange={(v) => void r.setTwist({ twists: v })}
            />
            <Slider
              label="Strand Thickness" min={0.5} max={2.0} step={0.05} unit="mm"
              value={state.twist.strandRadiusMM}
              onChange={(v) => void r.setTwist({ strandRadiusMM: v })}
            />
            <Slider
              label="Cross Amplitude" min={0} max={1.6} step={0.05} unit="mm"
              value={state.twist.amplitudeMM}
              onChange={(v) => void r.setTwist({ amplitudeMM: v })}
            />
            <p className="font-body text-[11px] leading-relaxed text-on-surface-variant">
              Pavé is set in the Gems tab and follows the lead strand of the twist.
            </p>
          </div>
        ) : null}
      </div>

      {/* ⚠️ Shank Style, Cathedral and Fit USED TO BE HERE, gated on `engRows`.
          They are engagement-only rows filed under a tab about the band's
          cross-section, three places above a tab called "Engagement" — which is
          how a user asked "where is cathedral setting?" about a control that had
          been live since W20. They now live in Engagement › Band, together and
          with the width, which is where the reference keeps its own four. Moved,
          not copied: two live copies of one control is how a dead one hides. */}

      {/* ── Cross-section profile (base section for the standard band) ── */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Cross-Section Profile</SectionLabel>
        {/* Three across so a two-word name ("Knife Edge", "Flat Court") fits
            without truncating — the letter is the catalog key, not a label a
            shopper can read, so it moves to the tooltip. */}
        <div className={`grid grid-cols-3 gap-2 ${crossover ? "pointer-events-none opacity-40" : ""}`}>
          {catalogs.profiles.map((p) => (
            <OptionCard
              key={p.id}
              title={p.id}
              selected={p.id === state.profileId}
              onClick={() => void apply(r.setProfile(p.id))}
            >
              <svg viewBox="0 0 60 40" width="44" height="30">
                <path d={p.iconPath} fill="none" stroke="#444748" strokeWidth="1.5" />
              </svg>
              <span className="text-center font-body text-[7.5px] leading-tight tracking-wide text-on-surface-variant">
                {p.label}
              </span>
            </OptionCard>
          ))}
          {/* v67 — the PARAMETRIC section: not a catalog preset (the viewer's
              list is the fixed shapes), so the host adds its card. Choosing it
              keeps whatever sliders the ring stored. */}
          {caps.ringBodyV67 ? (
            <OptionCard
              title="Parametric"
              selected={isParamProfile(state)}
              onClick={() => void apply(r.applyRingDesign(selectParamPatch()))}
            >
              <svg viewBox="8 6 44 28" width="44" height="30" preserveAspectRatio="none">
                <path d={paramSectionPath(resolvedParam(state), state.widthMM, state.thicknessMM)} fill="none" stroke="#444748" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
              </svg>
              <span className="text-center font-body text-[7.5px] leading-tight tracking-wide text-on-surface-variant">
                Your Own Shape
              </span>
            </OptionCard>
          ) : null}
        </div>
      </div>

      {caps.ringBodyV67 && isParamProfile(state) && !crossover ? <ParamSection /> : null}

      <FileButton
        accept=".3dm"
        full
        onFile={async (file) => {
          try {
            await apply(r.loadProfile3dm(file));
          } catch (err) {
            alert("Failed to parse .3dm: " + (err as Error).message);
          }
        }}
      >
        Upload .3dm Profile
      </FileButton>

      {caps.ringBuilderV37 ? (
        <Slider
          label="Comfort Fit"
          min={0}
          max={1}
          step={0.05}
          value={state.comfortFitMM ?? 0}
          display={(v) => (v <= 0 ? "Flat" : `${v.toFixed(2)} mm`)}
          disabled={crossover}
          onChange={(v) => void r.setComfortFit(v)}
        />
      ) : null}

      {/* ── Edge treatment ── */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Edge Treatment</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {catalogs.edgeStyles.map((e) => (
            <Chip
              key={e.id}
              selected={state.edgeStyle === e.id}
              onClick={() => void apply(r.setEdgeStyle(e.id as EdgeStyle))}
              className="flex-1"
            >
              {e.label}
            </Chip>
          ))}
        </div>
      </div>

      {/* ITEM 19 — the edge rows, moved here from the Joints tab. Their caps
          guards travel with them: a pre-v37 viewer has no `setJointGrooves`
          edge flag and a pre-v38 one has no `setEdgeSides`, and calling a method
          an older viewer lacks leaves the promise unresolved forever. The
          "overridden by Per-Side Edges in the Joints tab" hint that used to sit
          here is gone — the two rows are now three lines apart. */}
      {caps.ringBuilderV37 ? <EdgeGroovesRow /> : null}
      {caps.ringGeometryV38 ? <EdgeSidesRow /> : null}
      {!caps.ringGeometryV38 ? (
        <span className="font-body text-[10px] text-outline">
          Per-side edge treatments need viewer contract v38.
        </span>
      ) : null}

      {/* ── Decorative grooves (the shared cross-section lives in Joints › Separation) ── */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Decorative Grooves</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {catalogs.groovePatterns.map((g) => (
            <Chip
              key={g.id}
              selected={state.grooves.pattern === g.id}
              onClick={() => void apply(r.setGrooves({ pattern: g.id as GroovePattern }))}
              className="flex-1"
            >
              {g.label}
            </Chip>
          ))}
        </div>
        {state.grooves.pattern !== "none" ? (
          <div className="mt-1 flex flex-col gap-3">
            {/* Ranges MUST be the engine's own clamp bounds — width 0.25…3.00 (0.15… from v67),
                depth 0.10…1.00 (`clampGrooveConfig`, BandConfigurator.ts:172).
                These sliders used to declare 0.05…0.80 and 0.20…2.00, so the
                bottom of both tracks was DEAD: dragging below the engine floor
                wrote a value the engine immediately clamped, and the handle
                snapped back under the user's finger. They also write the exact
                same `state.grooves` field as the Joint Width / Joint Depth
                sliders in the Joints tab (`setJointGrooves` just delegates to
                `setGrooves`), which already declare the correct bounds — so the
                two rows disagreed about the range of one shared value. */}
            <Slider
              label="Groove Depth"
              min={0.1}
              max={1}
              step={0.05}
              unit="mm"
              value={state.grooves.depthMM}
              onChange={(v) => void r.setGrooves({ depthMM: v })}
            />
            {/* v67: the engine floor is 0.15 mm (`quarterGrooveSlider` —
                the same range the Joints tab's Joint Width row uses). */}
            <Slider
              label="Groove Width"
              min={quarterGrooveSlider(caps).min}
              max={3}
              step={quarterGrooveSlider(caps).step}
              unit="mm"
              value={state.grooves.widthMM}
              onChange={(v) => void r.setGrooves({ widthMM: v })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The live "Flat Court · 3.50 × 1.80 mm" header, with the profile drawn to
 * scale beside it.
 *
 * The outline comes from `ProfileItem.iconPath`, which the viewer builds with
 * `pointsToIconPath(points)` off the very point list the lathe sweeps — so the
 * card cannot disagree with the geometry, and a profile added to the engine
 * shows up here with no work. A `.3dm` upload sets `profileId: "custom"`, whose
 * points never crossed postMessage: the card keeps the numbers and says so
 * rather than drawing a shape it does not have.
 */
function ProfileSummary() {
  const { state, catalogs } = useRing();
  const param = isParamProfile(state);
  const profile = param
    ? { label: "Your Own Shape", iconPath: paramSectionPath(resolvedParam(state), state.widthMM, state.thicknessMM) }
    : catalogs.profiles.find((p) => p.id === state.profileId);
  const crossover = state.bandStyle === "crossover";

  return (
    <div className="flex items-center gap-4 rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-3">
      <div className="flex min-h-[52px] min-w-[52px] items-center justify-center">
        {profile ? (
          <ProfileSection path={profile.iconPath} widthMM={state.widthMM} thicknessMM={state.thicknessMM} />
        ) : (
          <span className="font-display text-[9px] uppercase tracking-[0.14em] text-outline">3dm</span>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-display text-[13px] font-semibold tracking-[0.04em] text-primary">
          {profile?.label ?? "Custom Profile"}
        </span>
        <span className="font-body text-[12px] tabular-nums text-on-surface-variant">
          {state.widthMM.toFixed(2)} × {state.thicknessMM.toFixed(2)} mm
        </span>
        {crossover ? (
          <span className="font-body text-[10px] text-outline">
            Cross-over: the strands replace the cross-section, silhouette and comfort fit.
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** v38 per-side edge treatments. Fixed, tiny list — no catalog round-trip. */
const SIDE_EDGE_OPTIONS: { id: SideEdgeStyle; label: string }[] = [
  { id: "none", label: "None" },
  { id: "edge", label: "Edge" },
  { id: "milgrain", label: "Milgrain" },
  { id: "milgrain2", label: "Milgrain ×2" },
];

/** The global Edge Treatment read as a per-side value — what both edges already
 * look like while `edgeSides` is absent. The per-side rows show this, and the
 * first write sends it on the side the user did NOT touch: `setEdgeSides` seeds
 * a missing side with "none", which would strip the band's own edge off it. */
const GLOBAL_EDGE_AS_SIDE: Record<EdgeStyle, SideEdgeStyle> = {
  sharp: "none",
  rounded: "edge",
  bevel: "edge",
  milgrain: "milgrain",
};

/**
 * The edge half of the same `GrooveConfig`, beside the Edge Treatment row it
 * belongs to (item 19). Same setter, same shared cross-section and size — only
 * the toggle lives here.
 */
function EdgeGroovesRow() {
  const { state, r, apply } = useRing();
  const g = state.grooves;

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionLabel>Edge Grooves</SectionLabel>
        <Toggle
          checked={g.edges ?? false}
          onChange={(v) => void apply(r.setJointGrooves({ edges: v }))}
          label="Carve a groove along both band edges"
        />
      </div>
      <span className="-mt-3 font-body text-[10px] text-outline">
        Shape and size come from Joints › Separation — one cross-section for every groove on the ring.
      </span>
    </>
  );
}

/**
 * v38 per-side edge treatment. Both rows are always live, as in the reference,
 * and while `edgeSides` is absent they show what the global Edge Treatment
 * (the row just above) is already doing to the two edges — touching one takes the
 * override, inheriting rather than resetting the side left alone. The toggle
 * only reattaches to the global, which is otherwise unreachable.
 */
function EdgeSidesRow() {
  const { state, r, apply } = useRing();
  const inherited = GLOBAL_EDGE_AS_SIDE[state.edgeStyle] ?? "none";
  const sides: EdgeSides = state.edgeSides ?? { left: inherited, right: inherited };
  const write = (patch: Partial<EdgeSides>) => void apply(r.setEdgeSides({ ...sides, ...patch }));

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <SectionLabel>Per-Side Edges</SectionLabel>
        <Toggle
          checked={!!state.edgeSides}
          onChange={(v) => void apply(r.setEdgeSides(v ? sides : null))}
          label="Treat each edge independently"
        />
      </div>
      <MixedOptionRow
        label="Left side"
        options={SIDE_EDGE_OPTIONS}
        selected={sides.left}
        onSelect={(id) => write({ left: id })}
      />
      <MixedOptionRow
        label="Right side"
        options={SIDE_EDGE_OPTIONS}
        selected={sides.right}
        onSelect={(id) => write({ right: id })}
      />
      {!state.edgeSides ? (
        <span className="font-body text-[10px] text-outline">
          Both edges currently follow the Edge Treatment above — changing a side here takes over
          from it.
        </span>
      ) : null}
    </>
  );
}

/**
 * v67 (band designs W3) — the parametric section's sliders. Every value shown
 * is what the engine BUILT (`clampParamProfile` on the stored sliders), and
 * every range is that clamp's, so the handle never snaps back. Each slider
 * sends one field through `applyRingDesign` (a merge); the kit batches the
 * drag into one high-resolution rebuild.
 */
function ParamSection() {
  const { state, r, apply } = useRing();
  const res = resolvedParam(state);
  const step = currentStep(res);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <div className="flex items-baseline justify-between">
        <SectionLabel>Shape the Section</SectionLabel>
        <button
          type="button"
          onClick={() => void apply(r.applyRingDesign(paramResetPatch()))}
          className="font-body text-[11px] text-primary underline underline-offset-2"
        >
          Reset
        </button>
      </div>
      {paramSliders(state.widthMM, state.thicknessMM).map((sl) => (
        <div key={sl.key} className="flex flex-col gap-1">
          <Slider
            label={sl.label}
            min={sl.min}
            max={sl.max}
            step={sl.step}
            value={Math.min(sl.max, res[sl.key])}
            display={sl.display}
            onChange={(v) => void r.applyRingDesign(paramPatch(sl.key, v))}
          />
          <span className="font-body text-[10px] text-outline">{sl.hint}</span>
        </div>
      ))}

      <div className="flex items-center justify-between gap-2">
        <span className="flex flex-col">
          <SectionLabel>One-Sided Step</SectionLabel>
          <span className="font-body text-[10px] text-outline">Lower one side of the face below a line running round the ring.</span>
        </span>
        <Toggle
          checked={!!step}
          label="One-sided step"
          onChange={(v) => void apply(r.applyRingDesign(v ? stepOnPatch(state.widthMM) : stepOffPatch()))}
        />
      </div>
      {step ? (
        <>
          <MixedOptionRow
            label="Lowered Side"
            options={[{ id: "-y", label: "Left" }, { id: "+y", label: "Right" }]}
            selected={step.side}
            onSelect={(id) => void apply(r.applyRingDesign(stepPatch(step, { side: id as "+y" | "-y" })))}
          />
          {stepSliders(state.widthMM, res, state.thicknessMM).map((sl) => (
            <Slider
              key={sl.key}
              label={sl.label}
              min={sl.min}
              max={sl.max}
              step={sl.step}
              unit={sl.unit}
              value={Math.min(sl.max, Math.max(sl.min, step[sl.key] ?? 90))}
              onChange={(v) => void r.applyRingDesign(stepPatch(step, { [sl.key]: v }))}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}
